import {_} from 'lodash';
import {Permission} from './Permission';
import {IButton, TRouteIdOrDefinition} from '../index';
import {getEadmRole} from '../util/misc';
import {global} from '../appContext';
import {nsRoute} from '../util/route';
import {IDropdownButton} from '../component/button';
import {getHash} from './routeHash';
import {getRouteDefinition, getRouteLabel} from './routeLookup';
import ButtonOrder = nsRoute.ButtonOrder;

export function getRouteButton(idOrDef: TRouteIdOrDefinition, config: Partial<IButton> = {}): IButton {
    const def = getRouteDefinition(idOrDef);
    if (!def) {
        return _.defaultsDeep(config, {
            label: config.label ?? '',
        }) as IButton;
    }
    return _.defaultsDeep(config, {
        icon: def.icon,
        label: config.label ?? getRouteLabel(idOrDef),
        permissionLevel: def.permissionLevel,
        href: getHash(idOrDef, global.router.currentRoute()?.__routeParams ?? {}) ?? '',
        validator: () => getEadmRole() >= (def.permissionLevel ?? Permission.None),
    } as IButton);
}

export function getRouteDropdownButton(
    idOrDef: TRouteIdOrDefinition,
    config: Partial<IDropdownButton> = {},
): IDropdownButton {
    const def = getRouteDefinition(idOrDef);
    if (!def) {
        return {...config} as IDropdownButton;
    }
    // Config last so callers can pass FEStandard syncfield keys (same dictionary as nav-left.*).
    return {
        cls: 'btn-action',
        description: () => def.description,
        href: () => getHash(idOrDef, global.router.currentRoute()?.__routeParams ?? {}) ?? '',
        icon: def.icon,
        label: () => getRouteLabel(idOrDef),
        validator: () => getEadmRole() >= (def.permissionLevel ?? Permission.None),
        ...config,
    } as IDropdownButton;
}

export function getBackButton(idOrDef?: TRouteIdOrDefinition, config: Partial<IButton> = {}): IButton {
    const parentId = idOrDef ?? global.router.currentRoute()?.__routeDef?.parent;
    if (!parentId) {
        return _.defaultsDeep(config, {
            label: config.label ?? '',
        }) as IButton;
    }
    const def = getRouteDefinition(parentId);
    if (!def) {
        return _.defaultsDeep(config, {
            label: config.label ?? '',
        }) as IButton;
    }
    return getRouteButton(def, _.defaultsDeep(config, {
        cssClass: 'action btn-back',
        description: `go back to|destination:((${getRouteLabel(def)}|ac))`,
        icon: 'fa-arrow-left',
        label: 'back',
        order: ButtonOrder.CANCEL,
    }));
}

export function getCancelButton(
    idOrDefOrFn?: TRouteIdOrDefinition | Function,
    config: Partial<IButton> = {},
): IButton | null {
    if (typeof idOrDefOrFn == 'function') {
        const onClick: Function = idOrDefOrFn;
        return _.defaultsDeep(config, {
            onClick,
            cssClass: 'action btn-back',
            icon: 'fa-ban',
            label: 'cancel',
            order: ButtonOrder.CANCEL,
        }) as IButton;
    }
    const _idOrDef = idOrDefOrFn ?? global.router.currentRoute()?.__routeDef?.parent;
    if (!_idOrDef) {
        return null;
    }
    const def = getRouteDefinition(_idOrDef);
    if (!def) {
        return null;
    }
    return getRouteButton(def, _.defaultsDeep(config, {
        cssClass: 'action btn-back',
        icon: 'fa-ban',
        label: 'cancel',
        order: ButtonOrder.CANCEL,
    }));
}

export function getExitButton(
    idOrDefOrFn?: TRouteIdOrDefinition | Function,
    config: Partial<IButton> = {},
): IButton | null {
    return getCancelButton(idOrDefOrFn, _.defaults(config, {
        icon: 'fa-times',
        label: 'exit',
    }));
}
